import React from 'react';
import { XIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, FileIcon, DollarSignIcon, UserIcon, CalendarIcon, ClockIcon, LinkIcon, ExternalLinkIcon, CreditCardIcon, BuildingIcon } from 'lucide-react';
import { LedgerEntry, LedgerSourceModule } from './LedgerView';
import { formatCurrency } from '../utils/currencyFormatter';
interface LedgerEntryDetailProps {
  entry: LedgerEntry;
  onClose: () => void;
  locale: string;
}
export function LedgerEntryDetail({
  entry,
  onClose,
  locale
}: LedgerEntryDetailProps) {
  // Get source module icon and color
  const getSourceModuleDetails = (module: LedgerSourceModule) => {
    switch (module) {
      case 'RapidAP':
        return {
          icon: <ReceiptIcon className="h-5 w-5" data-id="element-8145" />,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
          label: 'Accounts Payable'
        };
      case 'RapidPO':
        return {
          icon: <ShoppingCartIcon className="h-5 w-5" data-id="element-8146" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          label: 'Purchase Orders'
        };
      case 'RapidExpense':
        return {
          icon: <CreditCardIcon className="h-5 w-5" data-id="element-8147" />,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          label: 'Expenses'
        };
      case 'RapidContract':
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-8148" />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-100',
          label: 'Contracts'
        };
      case 'RapidPayment':
        return {
          icon: <DollarSignIcon className="h-5 w-5" data-id="element-8149" />,
          color: 'text-indigo-600',
          bgColor: 'bg-indigo-100',
          label: 'Payments'
        };
      case 'Manual':
        return {
          icon: <FileIcon className="h-5 w-5" data-id="element-8150" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          label: 'Manual Entry'
        };
      default:
        return {
          icon: <FileIcon className="h-5 w-5" data-id="element-8151" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          label: 'Other'
        };
    }
  };
  const sourceDetails = getSourceModuleDetails(entry.sourceModule);
  // Get status details
  const getStatusDetails = (status: string) => {
    switch (status) {
      case 'Draft':
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          label: 'Draft'
        };
      case 'Posted':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          label: 'Posted'
        };
      case 'Reversed':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          label: 'Reversed'
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          label: status
        };
    }
  };
  const statusDetails = getStatusDetails(entry.status);
  return <div className="fixed inset-0 overflow-hidden z-20" aria-labelledby="slide-over-title" role="dialog" aria-modal="true" data-id="element-8152">
      <div className="absolute inset-0 overflow-hidden" data-id="element-8153">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-8154"></div>
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-8155">
          <div className="relative w-screen max-w-2xl" data-id="element-8156">
            <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-auto" data-id="element-8157">
              {/* Header */}
              <div className="px-4 sm:px-6" data-id="element-8158">
                <div className="flex items-start justify-between" data-id="element-8159">
                  <h2 className="text-lg font-medium text-gray-900" id="slide-over-title" data-id="element-8160">
                    Ledger Entry Details
                  </h2>
                  <div className="ml-3 h-7 flex items-center" data-id="element-8161">
                    <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={onClose} data-id="element-8162">
                      <span className="sr-only" data-id="element-8163">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-8164" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Main content */}
              <div className="mt-6 relative flex-1 px-4 sm:px-6" data-id="element-8165">
                <div className="border-b border-gray-200 pb-5 mb-5" data-id="element-8166">
                  <div className="flex justify-between items-center" data-id="element-8167">
                    <div data-id="element-8168">
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-8169">
                        {entry.description}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-8170">
                        ID: {entry.id}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusDetails.bgColor} ${statusDetails.textColor}`} data-id="element-8171">
                      {statusDetails.label}
                    </span>
                  </div>
                </div>
                {/* Source information */}
                <div className="mb-6" data-id="element-8172">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8173">
                    SOURCE
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-8174">
                    <div className="flex items-center mb-2" data-id="element-8175">
                      <div className={`p-2 rounded-md ${sourceDetails.bgColor} ${sourceDetails.color} mr-3`} data-id="element-8176">
                        {sourceDetails.icon}
                      </div>
                      <div data-id="element-8177">
                        <p className="text-sm font-medium text-gray-900" data-id="element-8178">
                          {sourceDetails.label}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-8179">
                          {entry.sourceModule}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center" data-id="element-8180">
                      <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8181" />
                      <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-8182">
                        {entry.sourceReference}{' '}
                        <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-8183" />
                      </a>
                    </div>
                  </div>
                </div>
                {/* GL information */}
                <div className="mb-6" data-id="element-8184">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8185">
                    GL INFORMATION
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-8186">
                    <div className="grid grid-cols-2 gap-4" data-id="element-8187">
                      <div data-id="element-8188">
                        <p className="text-xs text-gray-500" data-id="element-8189">GL Code</p>
                        <p className="text-sm font-mono font-medium text-gray-900" data-id="element-8190">
                          {entry.glCode}
                        </p>
                      </div>
                      <div data-id="element-8191">
                        <p className="text-xs text-gray-500" data-id="element-8192">GL Path</p>
                        <p className="text-sm text-gray-900" data-id="element-8193">{entry.glPath}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Amount information */}
                <div className="mb-6" data-id="element-8194">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8195">
                    AMOUNT
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-8196">
                    <div className="grid grid-cols-2 gap-4" data-id="element-8197">
                      <div data-id="element-8198">
                        <p className="text-xs text-gray-500" data-id="element-8199">Debit</p>
                        <p className="text-lg font-mono font-medium text-green-600" data-id="element-8200">
                          {entry.debit ? formatCurrency(entry.debit, entry.currency, locale) : '-'}
                        </p>
                      </div>
                      <div data-id="element-8201">
                        <p className="text-xs text-gray-500" data-id="element-8202">Credit</p>
                        <p className="text-lg font-mono font-medium text-red-600" data-id="element-8203">
                          {entry.credit ? formatCurrency(entry.credit, entry.currency, locale) : '-'}
                        </p>
                      </div>
                      {entry.exchangeRate && <div className="col-span-2" data-id="element-8204">
                          <p className="text-xs text-gray-500" data-id="element-8205">Exchange Rate</p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-8206">
                            1 {entry.currency} = {entry.exchangeRate} USD
                          </p>
                        </div>}
                    </div>
                  </div>
                </div>
                {/* Additional details */}
                <div className="mb-6 grid grid-cols-2 gap-4" data-id="element-8207">
                  <div data-id="element-8208">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8209">
                      DATE INFORMATION
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-8210">
                      <div className="flex items-center mb-2" data-id="element-8211">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8212" />
                        <div data-id="element-8213">
                          <p className="text-xs text-gray-500" data-id="element-8214">
                            Transaction Date
                          </p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-8215">
                            {new Date(entry.date).toLocaleDateString(locale, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                          </p>
                        </div>
                      </div>
                      {entry.postedDate && <div className="flex items-center" data-id="element-8216">
                          <ClockIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8217" />
                          <div data-id="element-8218">
                            <p className="text-xs text-gray-500" data-id="element-8219">Posted Date</p>
                            <p className="text-sm font-medium text-gray-900" data-id="element-8220">
                              {new Date(entry.postedDate).toLocaleDateString(locale, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                            </p>
                          </div>
                        </div>}
                    </div>
                  </div>
                  <div data-id="element-8221">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8222">
                      ENTITY INFORMATION
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-8223">
                      {entry.entity && <div className="flex items-center mb-2" data-id="element-8224">
                          <BuildingIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8225" />
                          <div data-id="element-8226">
                            <p className="text-xs text-gray-500" data-id="element-8227">Entity</p>
                            <p className="text-sm font-medium text-gray-900" data-id="element-8228">
                              {entry.entity}
                            </p>
                          </div>
                        </div>}
                      {entry.site && <div className="flex items-center" data-id="element-8229">
                          <BuildingIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8230" />
                          <div data-id="element-8231">
                            <p className="text-xs text-gray-500" data-id="element-8232">Site</p>
                            <p className="text-sm font-medium text-gray-900" data-id="element-8233">
                              {entry.site}
                            </p>
                          </div>
                        </div>}
                    </div>
                  </div>
                </div>
                {/* Vendor information */}
                {entry.vendor && <div className="mb-6" data-id="element-8234">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8235">
                      VENDOR
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-8236">
                      <div className="flex items-center" data-id="element-8237">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8238" />
                        <div data-id="element-8239">
                          <p className="text-sm font-medium text-gray-900" data-id="element-8240">
                            {entry.vendor}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}
                {/* Audit information */}
                {entry.postedBy && <div className="mb-6" data-id="element-8241">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8242">
                      AUDIT INFORMATION
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-8243">
                      <div className="flex items-center" data-id="element-8244">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8245" />
                        <div data-id="element-8246">
                          <p className="text-xs text-gray-500" data-id="element-8247">Posted By</p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-8248">
                            {entry.postedBy}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}
                {/* Notes */}
                {entry.notes && <div className="mb-6" data-id="element-8249">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8250">
                      NOTES
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-8251">
                      <p className="text-sm text-gray-900" data-id="element-8252">{entry.notes}</p>
                    </div>
                  </div>}
                {/* Attachment */}
                {entry.attachmentUrl && <div className="mb-6" data-id="element-8253">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8254">
                      ATTACHMENTS
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-8255">
                      <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900 flex items-center" onClick={e => e.preventDefault()} data-id="element-8256">
                        <FileTextIcon className="h-4 w-4 mr-2" data-id="element-8257" />
                        View Document{' '}
                        <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-8258" />
                      </a>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}